/**
 * A calendar view: which objects fall on which day.
 *
 * `ACCEPTANCE.md`'s Notion row asks for "databases with every view type".
 * Notion ships six — table, board, list, gallery, calendar, timeline — and this
 * repo derived the first four from shape already. This is the fifth.
 *
 * It is a CAPABILITY rather than a presentation, and the line is worth being
 * precise about, because a calendar looks like a board and is not one. A
 * board's columns come from the objects: group by status and you get the
 * statuses that exist. A month's columns come from the calendar — seven of
 * them, whatever the data says, and twenty-eight to thirty-one cells whether or
 * not anything falls in them. Buckets fixed by the module rather than by the
 * content is exactly the boundary decision 0010 draws between the closed core
 * and the registry.
 */
import {
  CAPABILITIES, ROLE_KIND, type Role, type TLayoutSpec,
} from '@morph/spec';
import type { ObjectShape } from '@morph/generation';
import {
  handleOf, type BoundValues, type CapabilityInputs, type IR, type ObjectHandle,
} from '@morph/runtime';
import { withoutViewState } from './capability-view.js';

/** The date role a calendar should place objects by.
 *
 *  `date` first because it is the one every pool in this demo defines, then any
 *  other date-kinded role the shape happens to carry. Returns nothing when the
 *  shape has no dates at all, which is how the caller knows to refuse rather
 *  than to draw an empty month. */
export function calendarRole(shape: ObjectShape): Role | undefined {
  const dated = shape.roles.filter((r) => ROLE_KIND[r.role] === 'date');
  return dated.find((r) => r.role === 'date')?.role ?? dated[0]?.role;
}

/** The label a calendar event reads as. A title if the shape has one; anything
 *  textual otherwise. An event with no label is a coloured box on a day, which
 *  tells a reader that something happened and not what. */
export function calendarLabel(shape: ObjectShape): Role | undefined {
  const textual = shape.roles.filter((r) => ROLE_KIND[r.role] === 'text');
  return textual.find((r) => r.role === 'title')?.role ?? textual[0]?.role;
}

/**
 * A calendar day, `YYYY-MM-DD`, from whatever the store handed back.
 *
 * Deliberately NOT `new Date(v).toISOString()`. That path takes a plain
 * `YYYY-MM-DD` string, reads it as UTC midnight, and then — for any reader west
 * of Greenwich — a later `getDate()` returns the day before. A calendar whose
 * cells shift by one depending on where the reader is sitting is a calendar
 * that lies, and the bug is invisible in the time zone the author tested in.
 *
 * So a string that already carries a calendar day is trusted as one and never
 * round-tripped through a `Date`. A real `Date` object is read through its UTC
 * accessors, which is the same day the store wrote.
 */
export function calendarDay(v: unknown): string | undefined {
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return undefined;
    return v.toISOString().slice(0, 10);
  }
  if (typeof v === 'string') {
    // Already a calendar day, or an ISO timestamp that starts with one.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
    if (!m) return undefined;
    const [, y, mo, d] = m;
    const month = Number(mo), day = Number(d);
    if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
    return `${y}-${mo}-${d}`;
  }
  return undefined;
}

/** The month a set of days is "about": the one holding the most of them.
 *
 *  Not the earliest, and not today. A collection whose dates run 2019-2024
 *  opened on January 2019 shows an empty grid and reads as broken; opened on
 *  the current month it shows an empty grid and reads as broken in a way that
 *  changes tomorrow. The densest month is the one with something on it. */
export function busiestMonth(days: readonly string[]): string | undefined {
  const counts = new Map<string, number>();
  for (const d of days) {
    const key = d.slice(0, 7);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let best: string | undefined;
  let bestN = 0;
  // Ties break toward the EARLIER month, so the choice does not depend on the
  // order the store happened to return rows in.
  for (const key of [...counts.keys()].sort()) {
    const n = counts.get(key)!;
    if (n > bestN) { best = key; bestN = n; }
  }
  return best;
}

/** Replace a lens with a calendar over the same scope. */
export function calendarSpec(spec: TLayoutSpec, shape: ObjectShape): TLayoutSpec {
  const date = calendarRole(shape);
  const label = calendarLabel(shape);
  return {
    // Controls dropped with the root they drove — see `withoutViewState`.
    ...withoutViewState(spec),
    target: { ...spec.target, cardinality: 'large-set' },
    root: {
      kind: 'capability',
      id: 'pool-calendar',
      capability: 'calendar',
      version: CAPABILITIES['calendar'].version,
      config: { granularity: 'month', weekStart: 1, showEmpty: true },
      channels: {
        ...(date ? { date: { slot: 'cal-date', role: date, direction: 'read' } } : {}),
        ...(label ? { label: { slot: 'cal-label', role: label, direction: 'read' } } : {}),
      },
    },
  } as TLayoutSpec;
}

/**
 * Which objects fall on which day, resolved by the host.
 *
 * The renderer cannot do this: a date arrives as whatever the store holds, and
 * turning that into a calendar day has store rules and a time-zone trap the
 * module has no business knowing about.
 */
export function calendarInputs(
  ir: IR, rows: readonly BoundValues[],
  /* `undefined` for a reader — task `0182`. The module input's epoch is
     optional now, and this threads the host's MAY_WRITE gate through. */
  epoch: number | undefined, want?: string,
  /** The host's today, `YYYY-MM-DD`. Given, the host answers ANY month the
   *  module asks for — the app's calendar walks month by month and has a
   *  Today — and the module draws the adjacent-month steps and marks the
   *  day. Absent, only the months with objects are offered, as before. */
  today?: string,
): CapabilityInputs | undefined {
  const node = ir.nodes.find((n) => n.kind === 'capability' && n.capability === 'calendar');
  if (!node) return undefined;
  const dateRole = node.channels?.['date']?.role as Role | undefined;
  // No date bound means the pool has nothing to place, and the renderer says
  // so. An empty map still reaches it; `undefined` would render "data
  // unavailable", which is a different and less true sentence.
  if (!dateRole) return new Map();
  const labelRole = node.channels?.['label']?.role as Role | undefined;

  const events: { day: string; label: string; handle?: ObjectHandle }[] = [];
  let undated = 0;
  for (const row of rows) {
    // Two manifest keys can carry the same role, and an object populating both
    // gets that role back as an ARRAY. Which of two dates an object "happens
    // on" is a question about what the pool MEANS, and neither picking the
    // first nor drawing it twice is an answer this host can give honestly — so
    // it is counted as undated and SAID, the same call `continuousPlotInputs`
    // makes one capability over.
    if (Array.isArray(row[dateRole])) { undated += 1; continue; }
    const day = calendarDay(row[dateRole]);
    if (!day) { undated += 1; continue; }
    const rawLabel = labelRole === undefined ? undefined : row[labelRole];
    const label = typeof rawLabel === 'string' && rawLabel.trim()
      ? rawLabel.trim() : 'Untitled';
    const handle = handleOf(row);
    events.push({ day, label, ...(handle ? { handle } : {}) });
  }

  // Every month that has something in it, ascending — what the module offers
  // as a way out of the one on screen.
  const months = [...new Set(events.map((e) => e.day.slice(0, 7)))].sort();
  // The month asked for, if it still has anything in it. A month the reader
  // navigated to and then emptied by switching pools would otherwise draw a
  // blank grid with no way back.
  const anyMonth = today !== undefined;
  const month = (want && (months.includes(want) || (anyMonth && /^\d{4}-(0[1-9]|1[0-2])$/.test(want))) ? want : undefined)
    ?? busiestMonth(events.map((e) => e.day))
    // Nothing dated at all but a host that answers any month: the app shows
    // this month, empty, rather than refusing.
    ?? (anyMonth ? today.slice(0, 7) : undefined);

  // Nothing datable at all: hand the renderer the empty case rather than a
  // month invented so the grid has something to draw.
  if (!month) {
    return new Map([[node.i, {
      capability: 'calendar' as const,
      calendar: { month: '', events: [], ...(epoch === undefined ? {} : { epoch }), undated, elsewhere: 0, months: [] },
    }]]);
  }

  const here = events.filter((e) => e.day.startsWith(month));
  return new Map([[node.i, {
    capability: 'calendar' as const,
    calendar: {
      month,
      events: here,
      ...(epoch === undefined ? {} : { epoch }),
      undated,
      // Dated, but not on THIS grid. The count the films pool needed: one film
      // in the busiest month and eleven elsewhere, which a calendar that only
      // reported `undated` said nothing about at all.
      elsewhere: events.length - here.length,
      months,
      ...(today !== undefined ? { today } : {}),
    },
  }]]);
}
